import { API, graphqlOperation } from "aws-amplify";
import { createEvent, updateEvent } from "../graphql/mutations";

export class Event {
  constructor() {}

  async createEvent(companyId, { name, code, type }) {
    const {
      data: { createEvent: result }
    } = await API.graphql(
      graphqlOperation(createEvent, {
        input: {
          name: name.trim(),
          code: code.toLowerCase().trim(),
          type: type,
          status: "Draft",
          eventEventCompanyId: companyId,
          requireDob: true,
          isDeleted: 0,
          createdAt: new Date().toISOString()
        }
      })
    );
    return result;
  }

  async updateEventCode({ id, code }) {
    const {
      data: { updateEvent: result }
    } = await API.graphql(
      graphqlOperation(updateEvent, {
        input: {
          id: id,
          code: code
        }
      })
    );
    return result;
  }

  async deleteEvent({ id }) {
    try {
      const {
        data: { updateEvent: result }
      } = await API.graphql(
        graphqlOperation(updateEvent, {
          input: {
            id: id,
            isDeleted: 1
          }
        })
      );
      return result;
    } catch (err) {
      throw new Error(err.message);
    }
  }

  async getEventsById(id, nextToken) {
    const listEvents = /* GraphQL */ `
      query ListEvents(
        $filter: ModelEventFilterInput
        $limit: Int
        $nextToken: String
      ) {
        listEvents(filter: $filter, limit: $limit, nextToken: $nextToken) {
          items {
            eventCompany {
              id
              name
              eventClientAccess {
                items {
                  id
                  showPersonal
                }
              }
            }
            id
            name
            code
            status
            type
            iCalendar
            eventLocation(filter: { isDeleted: { eq: 0 } }) {
              items {
                id
                addressLine1
                postcode
                town
                createdAt
                eventSlotGroup(limit: 1000, filter: { isDeleted: { eq: 0 } }) {
                  items {
                    id
                    room
                    createdAt
                    date
                    capacity
                    eventSlot(limit: 1000, filter: { isDeleted: { eq: 0 } }) {
                      items {
                        id
                        length
                        time
                        type
                        booked
                        eventBooking(
                          limit: 1000
                          filter: { isDeleted: { eq: 0 } }
                        ) {
                          items {
                            id
                            firstname
                            lastname
                            email
                            dob
                            mobile
                            isDeleted
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            eventCollectUserData {
              items {
                id
                label
                validation
                fieldType
                createdAt
                isDeleted
                eventCollectUserDataOptions {
                  items {
                    id
                    options
                  }
                }
              }
            }
          }
          nextToken
        }
      }
    `;

    const {
      data: { listEvents: events }
    } = await API.graphql({
      query: listEvents,
      variables: {
        limit: 100,
        nextToken: nextToken,
        filter: {
          clientAccess: { contains: id },
          isDeleted: { eq: 0 }
        }
      },
      authMode: "API_KEY"
    });
    return events;
  }

  async getEventsByCompany({ id }) {
    const getEventCompany = `query GetEventCompany($id: ID!) {
      getEventCompany(id: $id) {
        event(
          filter: {
            isDeleted: { eq: 0 }
          }
        ) {
          items {
            id
            name
            code
            type
            status
            requireDob
            iCalendar
            status
            description
            commsTemplate
            createdAt
            isDeleted
          }
        }
      }
    }
    `;
    try {
      const {
        data: {
          getEventCompany: {
            event: { items: result }
          }
        }
      } = await API.graphql(
        graphqlOperation(getEventCompany, {
          id: id
        })
      );
      return result;
    } catch (err) {
      throw new Error(err.message);
    }
  }

  async getEventsByDateRange({ from, to, nextToken }) {
    const listEvents = /* GraphQL */ `
      query ListEvents(
        $filter: ModelEventFilterInput
        $limit: Int
        $nextToken: String
        $dateFrom: String
        $dateTo: String
      ) {
        listEvents(filter: $filter, limit: $limit, nextToken: $nextToken) {
          items {
            eventCompany {
              id
              name
            }
            id
            name
            code
            status
            type
            iCalendar
            attendance
            eventLocation(filter: { isDeleted: { eq: 0 } }) {
              items {
                id
                addressLine1
                postcode
                town
                createdAt
                eventSlotGroup(
                  limit: 1000
                  filter: {
                    isDeleted: { eq: 0 }
                    date: { between: [$dateFrom, $dateTo] }
                  }
                ) {
                  items {
                    id
                    room
                    createdAt
                    date
                    capacity
                    eventSlot(limit: 1000) {
                      items {
                        id
                        length
                        time
                        type
                        booked
                        eventBooking(
                          limit: 1000
                          filter: { isDeleted: { eq: 0 } }
                        ) {
                          items {
                            id
                            firstname
                            lastname
                            email
                            mobile
                            dob
                            attendance
                            customData
                            twentyFourHourReminder
                            isDeleted
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            eventCollectUserData {
              items {
                id
                label
                validation
                fieldType
                createdAt
                isDeleted
                eventCollectUserDataOptions {
                  items {
                    id
                    options
                  }
                }
              }
            }
          }
          nextToken
        }
      }
    `;
    const {
      data: { listEvents: events }
    } = await API.graphql(
      graphqlOperation(listEvents, {
        filter: { isDeleted: { eq: 0 } },
        nextToken: nextToken,
        dateFrom: from,
        dateTo: to
      })
    );
    return events;
  }

  async getEventsByConsultant({ date, consultant, nextToken }) {
    const eventQuery = `query ListEvents($assignedTo: String! $date: String $nextToken: String) {
      listEvents(
          limit:1000,
          filter: {
            isDeleted: { eq: 0 }
          },
          nextToken: $nextToken
      ) {
          items {
            eventCompany {
              id
              name
            },
            id
            name
            code
            status
            type
            iCalendar
            attendance
            eventLocation (
              filter: {
                isDeleted: { eq: 0 }
              }
            ) {
              items {
                  id
                  addressLine1
                  postcode
                  town
                  createdAt
                  eventSlotGroup(
                    limit: 1000
                    filter: {
                      isDeleted: { eq: 0 },
                      assignedTo: { contains: $assignedTo },
                      date: { gt: $date }
                    }
                  ) {
                    items {
                      id
                      room
                      createdAt
                      date
                      capacity
                      eventSlot(limit: 1000) {
                        items {
                            id
                            length
                            time
                            type
                            booked
                            eventBooking(
                              limit: 1000
                              filter: {
                                isDeleted: { eq: 0 }
                              }
                            ) {
                              items {
                                id
                                firstname
                                lastname
                                email
                                mobile
                                dob
                                attendance
                                customData
                                isDeleted
                              }
                            }
                          }
                      }
                    }
                  }
              }
          },
          eventCollectUserData {
            items {
              id,
              label,
              validation,
              fieldType,
              createdAt,
              isDeleted,
              eventCollectUserDataOptions {
                items {
                  id,
                  options
                }
              }
            }
          }
        }
        nextToken
      }
    }`;
    const {
      data: { listEvents: events }
    } = await API.graphql({
      query: eventQuery,
      variables: {
        assignedTo: consultant, // "Harley.cordner-young@healthyperformance.co.uk"
        date: date,
        nextToken: nextToken
      },
      authMode: "API_KEY"
    });
    return events;
  }

  async getEventsByEventSlotGroups({ date, nextToken }) {
    const listEventSlotGroups = /* GraphQL */ `
      query ListEventSlotGroups(
        $filter: ModelEventSlotGroupFilterInput
        $limit: Int
        $nextToken: String
      ) {
        listEventSlotGroups(
          filter: $filter
          limit: $limit
          nextToken: $nextToken
        ) {
          items {
            id
            room
            date
            capacity
            createdAt
            active
            isDeleted
            assignedTo
            updatedAt
            eventLocation {
              town
              isDeleted
              event {
                id
                name
                code
                status
                isDeleted
                eventCompany {
                  name
                  isDeleted
                }
              }
            }
          }
          nextToken
        }
      }
    `;
    const {
      data: { listEventSlotGroups: events }
    } = await API.graphql(
      graphqlOperation(listEventSlotGroups, {
        filter: { isDeleted: { eq: 0 }, date: { gt: date } },
        nextToken: nextToken
      })
    );

    events.items = events.items.filter(function(event) {
      if (event.eventLocation) {
        if (event.eventLocation.event) {
          return (
            event.eventLocation.event.isDeleted === 0 &&
            event.eventLocation.isDeleted === 0
          );
        }
      }
    });
    return events;
  }
}
